<template>
  <div
    :class="[
      'cms-tabs',
      `cms-tabs--${type}`,
      `cms-tabs--${tabPosition}`,
      {
        'cms-tabs--stretch': stretch,
      }
    ]"
  >
    <div class="cms-tabs__header">
      <div
        ref="navRef"
        class="cms-tabs__nav-wrap"
        :class="{
          'is-scrollable': scrollable,
        }"
      >
        <!-- 左右滚动按钮 -->
        <span
          v-if="scrollable"
          class="cms-tabs__nav-prev"
          :class="{ 'is-disabled': !scrollPrevAvailable }"
          @click="scrollPrev"
        >
          <i class="cms-icon-arrow-left"></i>
        </span>
        <span
          v-if="scrollable"
          class="cms-tabs__nav-next"
          :class="{ 'is-disabled': !scrollNextAvailable }"
          @click="scrollNext"
        >
          <i class="cms-icon-arrow-right"></i>
        </span>
        
        <!-- 标签栏 -->
        <div class="cms-tabs__nav-scroll" ref="navScrollRef" @scroll="onScroll">
          <div
            class="cms-tabs__nav"
            :class="{
              'is-stretch': stretch && ['top', 'bottom'].includes(tabPosition),
            }"
            ref="tabsNavRef"
          >
            <!-- 底部指示器 -->
            <div
              v-if="type === 'line'"
              class="cms-tabs__active-bar"
              :style="tabBarStyle"
            ></div>
            
            <!-- 标签项 -->
            <div
              v-for="(item, index) in panes"
              :key="item.name"
              class="cms-tabs__item"
              :class="{
                'is-active': item.name === currentName,
                'is-disabled': item.disabled,
                'is-closable': isClosable(item),
              }"
              :style="tabItemStyle(item)"
              @click="handleTabClick(item)"
              @mouseenter="trigger === 'hover' && !item.disabled && handleTabClick(item)"
            >
              <span>{{ item.label }}</span>
              <i
                v-if="isClosable(item)"
                class="cms-tabs__item-close cms-icon-close"
                @click.stop="removeTab(item.name)"
              ></i>
            </div>
            
            <!-- 添加按钮 -->
            <div
              v-if="addable"
              class="cms-tabs__new-tab"
              @click="handleTabAdd"
            >
              <i class="cms-icon-plus"></i>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="cms-tabs__content">
      <slot></slot>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, watch, provide, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { tabsProps, tabsEmits, tabsContextKey, type TabPaneProps } from './tabs'

defineOptions({
  name: 'CmsTabs'
})

const props = defineProps(tabsProps)
const emit = defineEmits(tabsEmits)

// 标签页数组
const panes = ref<TabPaneProps[]>([])

// 当前激活的标签名
const currentName = ref(props.modelValue || '')

// DOM 引用
const navRef = ref<HTMLElement | null>(null)
const navScrollRef = ref<HTMLElement | null>(null)
const tabsNavRef = ref<HTMLElement | null>(null)

// 滚动状态
const scrollable = ref(false)
const scrollPrevAvailable = ref(false)
const scrollNextAvailable = ref(false)

// 更新当前激活的标签
watch(
  () => props.modelValue,
  (val) => {
    if (val !== currentName.value) {
      currentName.value = val
      emit('tab-change', val)
    }
  }
)

// 注册标签页
const registerPane = (pane: TabPaneProps) => {
  if (pane.name) {
    const existPane = panes.value.find(item => item.name === pane.name)
    if (!existPane) {
      panes.value.push(pane)
      // 如果没有选中项，选择第一个
      if (!currentName.value && panes.value.length === 1) {
        currentName.value = pane.name
        emit('update:modelValue', currentName.value)
      }
    }
  }
  
  // 更新标签栏状态
  updateNavScroll()
}

// 注销标签页
const unregisterPane = (name: string | number) => {
  const index = panes.value.findIndex(item => item.name === name)
  if (index > -1) {
    panes.value.splice(index, 1)
    
    // 如果删除的是当前激活的标签，需要选择新的激活标签
    if (name === currentName.value) {
      let newName = ''
      if (panes.value.length) {
        if (index < panes.value.length) {
          // 选择下一个
          newName = panes.value[index].name
        } else {
          // 选择最后一个
          newName = panes.value[panes.value.length - 1].name
        }
      }
      
      currentName.value = newName
      emit('update:modelValue', newName)
    }
  }
  
  // 更新标签栏状态
  updateNavScroll()
}

// 处理标签点击
const handleTabClick = (pane: TabPaneProps) => {
  if (pane.disabled) return
  
  currentName.value = pane.name
  emit('update:modelValue', pane.name)
  emit('tab-click', pane)
}

// 处理标签关闭
const removeTab = (name: string | number) => {
  emit('tab-remove', name)
}

// 处理添加标签
const handleTabAdd = () => {
  emit('tab-add')
}

// 判断标签是否可关闭
const isClosable = (pane: TabPaneProps) => {
  if (pane.closable !== null) {
    return pane.closable
  }
  return props.closable
}

// 计算标签样式
const tabItemStyle = (item: TabPaneProps) => {
  const style: Record<string, string> = {}
  if (props.tabWidth) {
    style.width = `${props.tabWidth}px`
  }
  return style
}

// 计算激活指示器样式
const tabBarStyle = computed(() => {
  if (!navRef.value || !tabsNavRef.value || panes.value.length === 0) {
    return {
      transform: 'translateX(0px)',
      width: '0px'
    }
  }
  
  const activeTab = tabsNavRef.value.querySelector('.is-active')
  if (!activeTab) {
    return {
      transform: 'translateX(0px)',
      width: '0px'
    }
  }
  
  return {
    transform: `translateX(${activeTab.offsetLeft}px)`,
    width: `${activeTab.offsetWidth}px`
  }
})

// 滚动到激活的标签
const scrollToActiveTab = async () => {
  if (!navScrollRef.value || !tabsNavRef.value) return
  
  await nextTick()
  
  const activeTab = tabsNavRef.value.querySelector('.is-active')
  if (!activeTab) return
  
  const navScroll = navScrollRef.value
  const activeTabRect = activeTab.getBoundingClientRect()
  const navScrollRect = navScroll.getBoundingClientRect()
  
  // 判断是否需要滚动
  const isOutsideView = activeTabRect.left < navScrollRect.left || activeTabRect.right > navScrollRect.right
  
  if (isOutsideView) {
    // 计算需要滚动的位置
    const scrollLeft = activeTabRect.left - navScrollRect.left + navScroll.scrollLeft
    navScroll.scrollTo({
      left: scrollLeft,
      behavior: 'smooth'
    })
  }
}

// 向前滚动
const scrollPrev = () => {
  if (!navScrollRef.value || !scrollPrevAvailable.value) return
  
  const navScroll = navScrollRef.value
  const scrollDistance = navScroll.clientWidth / 2
  navScroll.scrollBy({
    left: -scrollDistance,
    behavior: 'smooth'
  })
}

// 向后滚动
const scrollNext = () => {
  if (!navScrollRef.value || !scrollNextAvailable.value) return
  
  const navScroll = navScrollRef.value
  const scrollDistance = navScroll.clientWidth / 2
  navScroll.scrollBy({
    left: scrollDistance,
    behavior: 'smooth'
  })
}

// 监听滚动事件
const onScroll = () => {
  updateScrollButtons()
}

// 更新滚动按钮状态
const updateScrollButtons = () => {
  if (!navScrollRef.value) return
  
  const navScroll = navScrollRef.value
  scrollPrevAvailable.value = navScroll.scrollLeft > 0
  scrollNextAvailable.value = navScroll.scrollLeft < navScroll.scrollWidth - navScroll.clientWidth
}

// 更新导航栏滚动状态
const updateNavScroll = async () => {
  await nextTick()
  
  if (!navScrollRef.value || !tabsNavRef.value) return
  
  const navScroll = navScrollRef.value
  const tabsNav = tabsNavRef.value
  
  scrollable.value = tabsNav.offsetWidth > navScroll.offsetWidth
  
  if (scrollable.value) {
    updateScrollButtons()
  }
  
  // 滚动到激活的标签
  scrollToActiveTab()
}

// 监听窗口大小变化
const handleResize = () => {
  updateNavScroll()
}

// 组件挂载后初始化
onMounted(() => {
  // 添加窗口大小变化监听
  window.addEventListener('resize', handleResize)
  
  // 初始化导航栏状态
  updateNavScroll()
})

// 组件卸载前清理
onBeforeUnmount(() => {
  window.removeEventListener('resize', handleResize)
})

// 提供上下文给子组件
provide(tabsContextKey, {
  currentName,
  registerPane,
  unregisterPane,
  handleClick: handleTabClick,
  handleClose: removeTab
})

// 监听标签变化，更新指示器位置
watch(
  () => currentName.value,
  async () => {
    await nextTick()
    scrollToActiveTab()
  }
)
</script>

<style>
.cms-tabs {
  --cms-tabs-header-height: 40px;
  --cms-tabs-active-color: #409eff;
  --cms-tabs-header-bg-color: #f5f7fa;
  --cms-tabs-border-color: #e4e7ed;
  --cms-tabs-hover-color: #ecf5ff;
  --cms-tabs-text-color: #303133;
  --cms-tabs-disabled-color: #c0c4cc;
  
  display: flex;
  flex-direction: column;
}

/* 标签位置样式 */
.cms-tabs--top {
  flex-direction: column;
}

.cms-tabs--right {
  flex-direction: row;
}

.cms-tabs--bottom {
  flex-direction: column-reverse;
}

.cms-tabs--left {
  flex-direction: row-reverse;
}

/* 标签头部 */
.cms-tabs__header {
  position: relative;
  margin: 0 0 15px;
}

.cms-tabs--bottom .cms-tabs__header {
  margin: 15px 0 0;
}

.cms-tabs--left .cms-tabs__header,
.cms-tabs--right .cms-tabs__header {
  height: 100%;
  width: auto;
  margin: 0 15px 0 0;
}

.cms-tabs--right .cms-tabs__header {
  margin: 0 0 0 15px;
}

/* 导航包装器 */
.cms-tabs__nav-wrap {
  position: relative;
  overflow: hidden;
}

.cms-tabs--left .cms-tabs__nav-wrap,
.cms-tabs--right .cms-tabs__nav-wrap {
  height: 100%;
}

/* 滚动区域 */
.cms-tabs__nav-scroll {
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-width: none; /* Firefox */
}

.cms-tabs__nav-scroll::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Edge */
}

.cms-tabs--left .cms-tabs__nav-scroll,
.cms-tabs--right .cms-tabs__nav-scroll {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
}

/* 导航区域 */
.cms-tabs__nav {
  position: relative;
  display: flex;
  white-space: nowrap;
}

.cms-tabs__nav.is-stretch {
  flex: 1;
  justify-content: space-between;
}

.cms-tabs--left .cms-tabs__nav,
.cms-tabs--right .cms-tabs__nav {
  flex-direction: column;
}

/* 滚动按钮 */
.cms-tabs__nav-prev,
.cms-tabs__nav-next {
  position: absolute;
  cursor: pointer;
  line-height: var(--cms-tabs-header-height);
  font-size: 12px;
  color: var(--cms-tabs-text-color);
  width: 20px;
  text-align: center;
  background-color: #fff;
  z-index: 1;
}

.cms-tabs__nav-prev.is-disabled,
.cms-tabs__nav-next.is-disabled {
  cursor: not-allowed;
  color: var(--cms-tabs-disabled-color);
}

.cms-tabs__nav-prev {
  left: 0;
}

.cms-tabs__nav-next {
  right: 0;
}

/* 标签项 */
.cms-tabs__item {
  padding: 0 20px;
  height: var(--cms-tabs-header-height);
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: relative;
  font-size: 14px;
  color: var(--cms-tabs-text-color);
  cursor: pointer;
  transition: color 0.3s;
}

.cms-tabs--left .cms-tabs__item,
.cms-tabs--right .cms-tabs__item {
  width: 100%;
  justify-content: flex-start;
}

.cms-tabs__item.is-active {
  color: var(--cms-tabs-active-color);
}

.cms-tabs__item:hover:not(.is-active):not(.is-disabled) {
  color: #909399;
}

.cms-tabs__item.is-disabled {
  color: var(--cms-tabs-disabled-color);
  cursor: not-allowed;
}

.cms-tabs__item.is-closable {
  padding-right: 30px;
}

/* 关闭按钮 */
.cms-tabs__item-close {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 12px;
  width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  border-radius: 50%;
  transition: all 0.3s;
}

.cms-tabs__item-close:hover {
  background-color: #c0c4cc;
  color: #fff;
}

/* 添加按钮 */
.cms-tabs__new-tab {
  margin-left: 10px;
  font-size: 14px;
  color: var(--cms-tabs-text-color);
  cursor: pointer;
  border: 1px dashed var(--cms-tabs-border-color);
  height: 24px;
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 2px;
  transition: all 0.3s;
  align-self: center;
}

.cms-tabs__new-tab:hover {
  color: var(--cms-tabs-active-color);
  border-color: var(--cms-tabs-active-color);
}

/* 激活指示器 */
.cms-tabs__active-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 2px;
  background-color: var(--cms-tabs-active-color);
  transition: transform 0.3s cubic-bezier(0.645, 0.045, 0.355, 1), width 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
}

.cms-tabs--left .cms-tabs__active-bar {
  top: 0;
  right: 0;
  width: 2px;
  height: auto;
}

.cms-tabs--right .cms-tabs__active-bar {
  top: 0;
  left: 0;
  width: 2px;
  height: auto;
}

.cms-tabs--bottom .cms-tabs__active-bar {
  top: 0;
  bottom: unset;
}

/* 内容区域 */
.cms-tabs__content {
  overflow: hidden;
  position: relative;
  flex: 1;
}

/* 卡片样式 */
.cms-tabs--card .cms-tabs__header {
  border-bottom: 1px solid var(--cms-tabs-border-color);
}

.cms-tabs--card .cms-tabs__item {
  border: 1px solid var(--cms-tabs-border-color);
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  margin-right: 2px;
  background-color: var(--cms-tabs-header-bg-color);
  transition: all 0.3s;
}

.cms-tabs--card .cms-tabs__item.is-active {
  background-color: #fff;
  border-bottom-color: #fff;
  color: var(--cms-tabs-active-color);
}

/* 带边框卡片样式 */
.cms-tabs--border-card {
  border: 1px solid var(--cms-tabs-border-color);
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.12), 0 0 6px 0 rgba(0, 0, 0, 0.04);
}

.cms-tabs--border-card .cms-tabs__header {
  background-color: var(--cms-tabs-header-bg-color);
  border-bottom: 1px solid var(--cms-tabs-border-color);
  margin: 0;
}

.cms-tabs--border-card .cms-tabs__item {
  transition: all 0.3s;
  border: 1px solid transparent;
  border-top: 0;
  margin: -1px -1px 0;
}

.cms-tabs--border-card .cms-tabs__item.is-active {
  background-color: #fff;
  border-right-color: var(--cms-tabs-border-color);
  border-left-color: var(--cms-tabs-border-color);
  color: var(--cms-tabs-active-color);
}

.cms-tabs--border-card .cms-tabs__content {
  padding: 15px;
}
</style>